import authService from './authService';

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GIS_SCRIPT_ID = 'google-identity-services';

export class GoogleIdentityError extends Error {
  constructor(code, message, retryable = false) {
    super(message);
    this.name = 'GoogleIdentityError';
    this.code = code;
    this.retryable = retryable;
  }
}

class GoogleIdentityService {
  constructor() {
    this.baseUrl = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test'
      ? ''
      : (process.env.REACT_APP_API_URL || 'https://api.astrantis.net').replace(/\/+$/, '');
    this.scriptPromise = null;
    this.challengeCache = new Map();
    this.listeners = {
      login: new Set(),
      link: new Set(),
    };
    this.initializedKey = '';
  }

  async getReadiness() {
    try {
      const response = await fetch(`${this.baseUrl}/api/config`, { credentials: 'include', cache: 'no-store' });
      if (!response.ok) return { enabled: false, reason: 'CONFIG_UNAVAILABLE' };
      const config = await response.json();
      const google = config?.oauth?.google;
      if (!google?.enabled || google.mode !== 'gis_id_token' || !google.clientId) {
        return { enabled: false, reason: 'GOOGLE_AUTH_DISABLED' };
      }
      return { enabled: true, config: google };
    } catch {
      return { enabled: false, reason: 'CONFIG_UNAVAILABLE' };
    }
  }

  loadScript() {
    if (window.google?.accounts?.id) return Promise.resolve(window.google);
    if (this.scriptPromise) return this.scriptPromise;

    this.scriptPromise = new Promise((resolve, reject) => {
      const existing = document.getElementById(GIS_SCRIPT_ID);
      const script = existing || document.createElement('script');
      let timeoutId;
      const cleanup = () => {
        clearTimeout(timeoutId);
        script.removeEventListener('load', handleLoad);
        script.removeEventListener('error', handleError);
      };
      const handleLoad = () => {
        cleanup();
        if (window.google?.accounts?.id) resolve(window.google);
        else reject(new GoogleIdentityError('GIS_SCRIPT_INVALID', 'Google 로그인 모듈을 초기화하지 못했습니다.'));
      };
      const handleError = () => {
        cleanup();
        this.scriptPromise = null;
        reject(new GoogleIdentityError('GIS_SCRIPT_FAILED', 'Google 로그인 모듈을 불러오지 못했습니다.', true));
      };
      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
      timeoutId = setTimeout(handleError, 10_000);
      if (!existing) {
        script.id = GIS_SCRIPT_ID;
        script.src = GIS_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.referrerPolicy = 'no-referrer-when-downgrade';
        document.head.appendChild(script);
      }
    });
    return this.scriptPromise;
  }

  async preflight(purpose) {
    const cached = this.challengeCache.get(purpose);
    if (cached && Date.now() - cached.createdAt < 8 * 60 * 1000) {
      return cached.promise;
    }
    const promise = this.requestPreflight(purpose).catch((error) => {
      this.challengeCache.delete(purpose);
      throw error;
    });
    this.challengeCache.set(purpose, { createdAt: Date.now(), promise });
    return promise;
  }

  async requestPreflight(purpose) {
    const headers = { 'Content-Type': 'application/json' };
    if (purpose === 'link') {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new GoogleIdentityError('REAUTH_REQUIRED', 'Google 계정을 연결하려면 다시 로그인해주세요.');
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(`${this.baseUrl}/api/auth/google/preflight`, {
      method: 'POST',
      headers,
      credentials: 'include',
      cache: 'no-store',
      body: JSON.stringify({ purpose, origin: window.location.origin }),
    });
    const data = await this.readJson(response);
    if (!response.ok) throw this.fromResponse(data, 'Google 로그인 준비에 실패했습니다.');
    return data;
  }

  async submitCredential(purpose, credential, challengeId) {
    const headers = { 'Content-Type': 'application/json' };
    const path = purpose === 'link'
      ? '/api/user-connections/google'
      : '/api/auth/google';
    const endpoint = `${this.baseUrl}${path}`;
    if (purpose === 'link') {
      const token = localStorage.getItem('auth_token');
      if (!token) throw new GoogleIdentityError('REAUTH_REQUIRED', 'Google 계정을 연결하려면 다시 로그인해주세요.');
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({ credential, challengeId }),
      });
      const data = await this.readJson(response);
      if (!response.ok) throw this.fromResponse(data, 'Google 로그인에 실패했습니다.');
      if (purpose === 'login') authService.saveUserData(data);
      return data;
    } finally {
      this.challengeCache.delete(purpose);
      this.initializedKey = '';
    }
  }

  async renderButton(element, options = {}) {
    const purpose = options.purpose === 'link' ? 'link' : 'login';
    const readiness = await this.getReadiness();
    if (!readiness.enabled) {
      throw new GoogleIdentityError(readiness.reason, 'Google 로그인이 현재 준비되지 않았습니다.');
    }
    const [google, challenge] = await Promise.all([this.loadScript(), this.preflight(purpose)]);
    if (!element) throw new GoogleIdentityError('BUTTON_TARGET_MISSING', 'Google 로그인 버튼을 표시하지 못했습니다.');
    const listener = { onSuccess: options.onSuccess, onError: options.onError };
    this.listeners[purpose].add(listener);
    element.replaceChildren();
    const initializationKey = `${purpose}:${challenge.challengeId}`;
    if (this.initializedKey !== initializationKey) {
      google.accounts.id.initialize({
        client_id: readiness.config.clientId,
        nonce: challenge.nonce,
        ux_mode: 'popup',
        use_fedcm_for_prompt: true,
        cancel_on_tap_outside: false,
        callback: async ({ credential }) => {
          try {
            if (!credential) throw new GoogleIdentityError('GOOGLE_TOKEN_INVALID', 'Google 인증 정보가 없습니다.');
            const result = await this.submitCredential(purpose, credential, challenge.challengeId);
            for (const current of this.listeners[purpose]) {
              if (current.onSuccess) current.onSuccess(result);
            }
          } catch (error) {
            for (const current of this.listeners[purpose]) {
              if (current.onError) current.onError(error);
            }
          }
        },
      });
      this.initializedKey = initializationKey;
    }
    google.accounts.id.renderButton(element, {
      type: 'standard',
      theme: readiness.config.button?.theme || 'outline',
      size: readiness.config.button?.size || 'large',
      text: purpose === 'link' ? 'continue_with' : 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: Math.max(200, Math.min(Number(options.width) || 320, 400)),
    });
    return {
      enabled: true,
      challengeId: challenge.challengeId,
      dispose: () => this.listeners[purpose].delete(listener),
    };
  }

  async readJson(response) {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new GoogleIdentityError('INVALID_RESPONSE', 'Google 인증 서버 응답을 확인하지 못했습니다.', true);
    }
    return response.json();
  }

  fromResponse(data, fallback) {
    return new GoogleIdentityError(
      data?.code || 'GOOGLE_AUTH_FAILED',
      data?.message || fallback,
      data?.retryable === true,
    );
  }

  resetForTests() {
    this.scriptPromise = null;
    this.challengeCache.clear();
    this.listeners.login.clear();
    this.listeners.link.clear();
    this.initializedKey = '';
  }
}

const googleIdentityService = new GoogleIdentityService();
export default googleIdentityService;
